import { BoxGeometry, DynamicDrawUsage, InstancedBufferAttribute, InstancedMesh, Matrix4, MeshPhongMaterial, Color, Sphere, Vector3 } from 'three';
import Experience from '../Experience.js';
import params, { BLOOM_LAYER } from '../params.js';
import gsap from 'gsap';

const DEPTH = 0.4;

const _inverse = new Matrix4();
const _origin = new Vector3();
const _direction = new Vector3();
const _sphere = new Sphere();

/**
 * The 3D text, built one box per block of the glyph.
 *
 * unibody is a pixel font: every glyph is a set of separate, uniform,
 * axis-aligned squares - 'W' alone is 45 of them, and there is not a curve in
 * the whole face. TextGeometry threw that structure away, merging the lot into
 * one buffer, so there was nothing left to move but the whole word.
 *
 * Font.generateShapes still does the glyph layout and kerning, but here each
 * shape it returns is read back as a square and becomes one instance of a box.
 * Same pixels on screen - the blocks tile edge to edge exactly as the extruded
 * outline did - but now every block is independently addressable, which is what
 * lets the cursor swell run per block rather than per word.
 */
export default class Text {
    constructor(text, size, color, position, name) {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.raycaster = this.experience.raycaster;
        this.resources = this.experience.resources;
        this.pointer = this.experience.pointer;
        this.size = size;

        this.setBlocks(text, size);
        this.setMaterial(color, name);
        this.setMesh(position, name);
    }

    /**
     * How wide and how tall a string is at size 1, so a caller can solve for
     * the size that makes it span a given width or read at a given height.
     * Glyph layout scales linearly with size, so the one measurement covers
     * every size the string might be built at.
     *
     * The height is the one to size by when strings are meant to compare:
     * width is a function of how many characters a string happens to have, so
     * two strings sharing a width span come out at wildly different letters.
     */
    static measure(text) {
        const font = new Experience().resources.items.mainFont;
        const shapes = font.generateShapes(text, 1);

        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (let i = 0; i < shapes.length; i++) {
            const points = shapes[i].getPoints();
            for (let p = 0; p < points.length; p++) {
                if (points[p].x < minX) minX = points[p].x;
                if (points[p].x > maxX) maxX = points[p].x;
                if (points[p].y < minY) minY = points[p].y;
                if (points[p].y > maxY) maxY = points[p].y;
            }
        }
        return { width: maxX - minX, height: maxY - minY };
    }

    /** Read the font's glyph squares back out as block centres and extents. */
    setBlocks(text, size) {
        const shapes = this.resources.items.mainFont.generateShapes(text, size);

        this.count = shapes.length;
        this.blocks = new Float32Array(this.count * 4); // centre x, centre y, w, h

        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

        for (let i = 0; i < this.count; i++) {
            const points = shapes[i].getPoints();
            let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
            for (let p = 0; p < points.length; p++) {
                const point = points[p];
                if (point.x < x0) x0 = point.x;
                if (point.x > x1) x1 = point.x;
                if (point.y < y0) y0 = point.y;
                if (point.y > y1) y1 = point.y;
            }
            const o = i * 4;
            this.blocks[o] = (x0 + x1) * 0.5;
            this.blocks[o + 1] = (y0 + y1) * 0.5;
            this.blocks[o + 2] = x1 - x0;
            this.blocks[o + 3] = y1 - y0;

            if (x0 < minX) minX = x0;
            if (x1 > maxX) maxX = x1;
            if (y0 < minY) minY = y0;
            if (y1 > maxY) maxY = y1;
        }

        // Match what TextGeometry.center() used to do, so every string keeps
        // the position it was placed at in World.
        const cx = (minX + maxX) * 0.5;
        const cy = (minY + maxY) * 0.5;
        for (let i = 0; i < this.count; i++) {
            this.blocks[i * 4] -= cx;
            this.blocks[i * 4 + 1] -= cy;
        }

        this.bounds = {
            minX: minX - cx, maxX: maxX - cx,
            minY: minY - cy, maxY: maxY - cy
        };

        this.fill = params.textBlockFill;
        const swell = params.textSwell;
        this.radius = size * swell.radius;
        this.grow = swell.grow;
        this.bulge = size * swell.bulge;
        this.spread = size * swell.spread;
        this.response = swell.response;

        // Per block: growth, then the x/y/z it has been nudged by.
        this.state = new Float32Array(this.count * 4);
        this.settled = true;

        // For the raycast early-out. Generous enough to cover a block at full
        // swell, so the cheap test never rejects something the slow test wants.
        const halfW = (maxX - minX) * 0.5;
        const halfH = (maxY - minY) * 0.5;
        this.localRadius = Math.sqrt(halfW * halfW + halfH * halfH + DEPTH * DEPTH)
            + this.bulge + this.spread + size * this.grow;
    }

    setMaterial(color, name) {
        this.material = new MeshPhongMaterial({
            color: color,
            shininess: 0,
            reflectivity: 0,
            fog: false,
            flatShading: true,
        });
        this.material.userData.color = new Color(color);

        const hover = params.textHover[name];
        this.hoverColor = hover ? new Color(hover) : null;
        if (!this.hoverColor) return;

        // The hover colour is mixed in per block, by the same amount the block
        // is swollen, so it arrives with the wave and fades out with it.
        //
        // This is a shader mix rather than instanceColor because instanceColor
        // multiplies: getting from a link's own colour to the hover colour that
        // way needs a per-channel ratio, which blows up on the channels these
        // colours have at zero. Mixing leaves material.color alone, so an
        // untouched word renders exactly as it did.
        this.material.onBeforeCompile = (shader) => {
            shader.uniforms.uHoverColor = { value: this.hoverColor };
            shader.vertexShader = 'attribute float aTint;\nvarying float vTint;\n'
                + shader.vertexShader.replace('#include <begin_vertex>',
                    '#include <begin_vertex>\n\tvTint = aTint;');
            // Blended round the hue wheel rather than through RGB. A straight
            // RGB mix of two saturated colours puts energy in all three
            // channels halfway across, and at exposure 15 the Reinhard curve
            // pushes that to white - so the wave came out white-edged with only
            // its peak on colour. Interpolating hue, saturation and value keeps
            // the midpoints as saturated as the two ends, so the colour sweeps
            // rather than washing out.
            const HSV = [
                'vec3 tintRgb2Hsv(vec3 c) {',
                '  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);',
                '  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));',
                '  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));',
                '  float d = q.x - min(q.w, q.y);',
                '  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + 1e-10)), d / (q.x + 1e-10), q.x);',
                '}',
                'vec3 tintHsv2Rgb(vec3 c) {',
                '  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);',
                '  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);',
                '  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);',
                '}',
                ''
            ].join('\n');

            const MIX = [
                'vec3 tintA = tintRgb2Hsv( diffuse );',
                'vec3 tintB = tintRgb2Hsv( uHoverColor );',
                'float tintDh = tintB.x - tintA.x;',
                'tintDh -= floor( tintDh + 0.5 );',
                'vec3 tintHsv = vec3( tintA.x + tintDh * vTint,',
                '  mix( tintA.y, tintB.y, vTint ), mix( tintA.z, tintB.z, vTint ) );',
                'vec4 diffuseColor = vec4( tintHsv2Rgb( tintHsv ), opacity );'
            ].join('\n');

            shader.fragmentShader = 'uniform vec3 uHoverColor;\nvarying float vTint;\n' + HSV
                + shader.fragmentShader.replace(
                    'vec4 diffuseColor = vec4( diffuse, opacity );', MIX);
        };
    }

    setMesh(position, name) {
        this.mesh = new InstancedMesh(new BoxGeometry(1, 1, DEPTH), this.material, this.count);
        this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
        // The swell pushes blocks outside the bounds three would derive from a
        // unit box, and the whole string is one draw call regardless.
        this.mesh.frustumCulled = false;

        if (this.hoverColor) {
            this.tint = new InstancedBufferAttribute(new Float32Array(this.count), 1);
            this.tint.setUsage(DynamicDrawUsage);
            this.mesh.geometry.setAttribute('aTint', this.tint);
        }

        this.writeMatrices();

        this.mesh.layers.enable(BLOOM_LAYER);
        this.mesh.position.set(position.x, position.y, position.z);
        gsap.from(this.mesh.rotation, 0.5, {
            x: -Math.PI * 0.5,
            ease: "back.inOut(1.7)",
        });

        // InstancedMesh.raycast walks every instance, and the raycaster runs
        // over every text each frame. Reject the whole string on one sphere
        // test first, so only the string actually under the cursor pays.
        const instancedRaycast = this.mesh.raycast.bind(this.mesh);
        this.mesh.raycast = (raycaster, intersects) => {
            _sphere.center.set(0, 0, 0);
            _sphere.radius = this.localRadius;
            _sphere.applyMatrix4(this.mesh.matrixWorld);
            if (!raycaster.ray.intersectsSphere(_sphere)) return;
            instancedRaycast(raycaster, intersects);
        };

        this.raycaster.objectsToTest.push(this.mesh);
        if (name) {
            this.mesh.name = name;
        }
        this.scene.add(this.mesh);
    }

    /**
     * Every block is an axis-aligned box, so its matrix is a scale and a
     * translation and nothing else - cheaper to write the twelve numbers than
     * to compose it through an Object3D, which matters at ~1600 blocks a frame.
     */
    writeMatrices() {
        const m = this.mesh.instanceMatrix.array;
        const blocks = this.blocks;
        const state = this.state;

        for (let i = 0; i < this.count; i++) {
            const b = i * 4;
            const o = i * 16;
            const g = 1 + state[b];

            // Depth is left at full scale - the gap is between neighbours in
            // the plane of the text, not along the extrusion.
            m[o] = blocks[b + 2] * g * this.fill;
            m[o + 1] = 0; m[o + 2] = 0; m[o + 3] = 0;
            m[o + 4] = 0;
            m[o + 5] = blocks[b + 3] * g * this.fill;
            m[o + 6] = 0; m[o + 7] = 0;
            m[o + 8] = 0; m[o + 9] = 0;
            m[o + 10] = g;
            m[o + 11] = 0;
            m[o + 12] = blocks[b] + state[b + 1];
            m[o + 13] = blocks[b + 1] + state[b + 2];
            m[o + 14] = state[b + 3];
            m[o + 15] = 1;
        }
        this.mesh.instanceMatrix.needsUpdate = true;
    }

    update(delta) {
        if (!this.mesh.visible) {
            if (!this.settled) {
                this.state.fill(0);
                this.settled = true;
                if (this.tint) {
                    this.tint.array.fill(0);
                    this.tint.needsUpdate = true;
                }
                this.writeMatrices();
            }
            return;
        }

        // Where the cursor crosses the plane the text lies in, in the text's
        // own space - so the swell tracks the word however the camera is
        // orbited, and a cursor that misses the plane entirely does nothing.
        let hitX = 0;
        let hitY = 0;
        let over = false;

        if (this.pointer.active) {
            _inverse.copy(this.mesh.matrixWorld).invert();
            _origin.copy(this.pointer.origin).applyMatrix4(_inverse);
            _direction.copy(this.pointer.origin).add(this.pointer.direction)
                .applyMatrix4(_inverse).sub(_origin);

            if (Math.abs(_direction.z) > 1e-6) {
                const t = -_origin.z / _direction.z;
                if (t > 0) {
                    hitX = _origin.x + _direction.x * t;
                    hitY = _origin.y + _direction.y * t;
                    const r = this.radius;
                    over = hitX > this.bounds.minX - r && hitX < this.bounds.maxX + r &&
                           hitY > this.bounds.minY - r && hitY < this.bounds.maxY + r;
                }
            }
        }

        // Nothing to push and nothing still moving.
        if (!over && this.settled) return;

        const blocks = this.blocks;
        const state = this.state;
        const radius = this.radius;
        // Exponential approach, so blocks trail the cursor slightly and the
        // swell reads as a wave crossing the word rather than a rigid mask.
        const k = 1 - Math.exp(-this.response * delta);

        let peak = 0;

        for (let i = 0; i < this.count; i++) {
            const b = i * 4;

            let grow = 0;
            let tx = 0;
            let ty = 0;
            let tz = 0;

            if (over) {
                const dx = blocks[b] - hitX;
                const dy = blocks[b + 1] - hitY;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < radius) {
                    // Raised cosine: flat at the peak and flat at the rim, so
                    // the swell is a smooth dome and nothing steps as a block
                    // enters or leaves. This is what makes neighbouring blocks
                    // grow a little and the one under the cursor grow most.
                    const f = 0.5 * (1 + Math.cos(Math.PI * dist / radius));
                    grow = this.grow * f;
                    tz = this.bulge * f;

                    // Sideways spread is zero at the centre - where "away from
                    // the cursor" means nothing - and fades out at the rim, so
                    // the middle lifts straight up and the ring around it opens
                    // outwards. That's the curve of the swell.
                    if (dist > 1e-5) {
                        const radial = this.spread * f * (dist / radius);
                        tx = (dx / dist) * radial;
                        ty = (dy / dist) * radial;
                    }
                }
            }

            state[b] += (grow - state[b]) * k;
            state[b + 1] += (tx - state[b + 1]) * k;
            state[b + 2] += (ty - state[b + 2]) * k;
            state[b + 3] += (tz - state[b + 3]) * k;

            // Normalised swell, which is exactly the falloff the wave is drawn
            // with - so the colour reaches furthest where the warp does.
            if (this.tint) this.tint.array[i] = this.grow > 0 ? state[b] / this.grow : 0;

            const e = Math.abs(state[b]) + Math.abs(state[b + 1])
                + Math.abs(state[b + 2]) + Math.abs(state[b + 3]);
            if (e > peak) peak = e;
        }
        if (this.tint) this.tint.needsUpdate = true;

        this.settled = peak < 1e-4;
        if (this.settled) this.state.fill(0);
        this.writeMatrices();
    }
}
